import { Vec2 } from 'features/assetViz/utils/helpers'
import React from 'react'
import * as THREE from 'three'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoLegacyBoilerWalls,
  GeckoVerticalTankShell,
  Section,
  Sectionable,
  TUBESPACING_DEFAULT,
} from 'types'

export function GetSectionable(
  component: GeckoComponentModel | undefined
): Sectionable | undefined {
  if (!component) return undefined

  if (component.type === GeckoComponentModelType.VerticalTankShell) {
    const shellModel = component as GeckoVerticalTankShell
    return {
      splitByX: false,
      vertexSpaceScale: 1,
      padMin: 0.001,
      padMax: 0.001,
      sections: shellModel.coursesArray
        .toSorted((a, b) => b.geometry.y.min.minus(a.geometry.y.min).num)
        .map((course) => {
          return {
            name: course.name,
            min: course.geometry.y.min,
            max: course.geometry.y.max,
          }
        }),
    }
  }

  if (component.type === GeckoComponentModelType.LegacyBoilerWalls) {
    const boilerModel = component as GeckoLegacyBoilerWalls
    return {
      splitByX: true,
      padMin: 1,
      padMax: 0,
      vertexSpaceScale: boilerModel.tubeSpacing?.num ?? TUBESPACING_DEFAULT.num,
      sections: boilerModel.wallsArray
        .toSorted((a, b) => a.geometry.x.min.minus(b.geometry.x.min).num)
        .map((wall) => {
          return {
            name: wall.name,
            min: wall.geometry.x.min,
            max: wall.geometry.x.max,
          }
        }),
    }
  }

  return undefined
}

export class ClipConfig {
  /** The single section to display, when set. */
  displayedSection: string | undefined

  rangeX: Vec2 | undefined
  rangeY: Vec2 | undefined

  rangeXRef: React.MutableRefObject<Vec2 | null>
  rangeYRef: React.MutableRefObject<Vec2 | null>

  /** The clipping range, unscaled by any special cases (ex. tube spacing) */
  unscaledRangeX: Vec2 | undefined
  unscaledRangeY: Vec2 | undefined

  fullRangeX: Vec2 | undefined
  fullRangeY: Vec2 | undefined

  planes: THREE.Plane[]

  public constructor() {
    this.rangeXRef = React.createRef<Vec2 | null>()
    this.rangeYRef = React.createRef<Vec2 | null>()
    this.planes = []

    this.clear()
  }

  public clear() {
    this.displayedSection = undefined
    this.rangeX = undefined
    this.rangeY = undefined
    this.rangeXRef.current = null
    this.rangeYRef.current = null
    this.unscaledRangeX = undefined
    this.unscaledRangeY = undefined
    this.planes = []
  }

  public isSet() {
    return this.displayedSection !== undefined || this.planes.length > 0
  }

  public updateFromSection(sectionable: Sectionable, section: Section) {
    const fullRange = sectionable.sections
      .map((s) => [s.min.num, s.max.num] as Vec2)
      .reduce((a, b) => [Math.min(a[0], b[0]), Math.max(a[1], b[1])], [
        Infinity,
        -Infinity,
      ] as Vec2)
    this.fullRangeX = sectionable.splitByX ? fullRange : undefined
    this.fullRangeY = sectionable.splitByX ? undefined : fullRange

    const min =
      (section.min.num - sectionable.padMin) * sectionable.vertexSpaceScale
    const max =
      (section.max.num + sectionable.padMax) * sectionable.vertexSpaceScale
    this.displayedSection = section.name
    this.rangeX = sectionable.splitByX ? ([min, max] as Vec2) : undefined
    this.rangeY = sectionable.splitByX ? undefined : ([min, max] as Vec2)
    this.rangeXRef.current = this.rangeX === undefined ? null : [min, max]
    this.rangeYRef.current = this.rangeY === undefined ? null : [min, max]

    this.unscaledRangeX = sectionable.splitByX
      ? ([section.min.num, section.max.num] as Vec2)
      : undefined
    this.unscaledRangeY = sectionable.splitByX
      ? undefined
      : ([section.min.num, section.max.num] as Vec2)

    this.planes = sectionable.splitByX
      ? [
          new THREE.Plane(new THREE.Vector3(1, 0, 0), -min),
          new THREE.Plane(new THREE.Vector3(-1, 0, 0), max),
        ]
      : [
          new THREE.Plane(new THREE.Vector3(0, 1, 0), -min),
          new THREE.Plane(new THREE.Vector3(0, -1, 0), max),
        ]
  }
}
